/**
 * User API - CRUD for User Management
 */
import { apiGet, apiGetList, apiPost, apiPut, apiDelete } from "./client";
import type { User, PaginatedResponse } from "@/types";

export interface UserListParams {
  page?: number;
  per_page?: number;
  role?: string;
  status?: string;
  search?: string;
  service_location_id?: string;
  exclude_patients?: boolean;
}

export interface CreateUserRequest {
  organization_id: string;
  username: string;
  email: string;
  password?: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  title?: string;
  suffix?: string;
  role: string;
  npi?: string;
  specialty?: string;
  taxonomy?: string;
  state_license_number?: string;
  phone?: string;
  facility_id?: string;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  break_glass_enabled?: boolean;
  department?: string;
  selected_roles?: string[];
  permissions?: string[];
  products?: string[];
  avatar_url?: string;
  patient_restricted?: boolean;
  patient_ids?: string[];
  bio?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  /** none | require_unlock | timed */
  lock_mode?: "none" | "require_unlock" | "timed";
  lock_after_hours?: number;
  access_expires_at?: string;
}

export interface LockUserRequest {
  mode: "until_unlock" | "timed";
  lock_after_hours?: number;
  access_expires_at?: string;
}

export interface UpdateUserRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  title?: string;
  suffix?: string;
  role?: string;
  npi?: string;
  specialty?: string;
  taxonomy?: string;
  state_license_number?: string;
  phone?: string;
  facility_id?: string;
  active?: boolean;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  break_glass_enabled?: boolean;
  department?: string;
  selected_roles?: string[];
  permissions?: string[];
  products?: string[];
  avatar_url?: string;
  patient_restricted?: boolean;
  patient_ids?: string[];
  bio?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  availability?: string;
  leave_status?: string;
}

/** NPPES registry lookup result for a provider NPI. */
export interface VerifyNPIResponse {
  npi: string;
  valid_format: boolean;
  found: boolean;
  active: boolean;
  enumeration_type?: string; // NPI-1 (individual) | NPI-2 (organization)
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  credential?: string;
  organization_name?: string;
  taxonomy_code?: string;
  taxonomy_desc?: string;
  taxonomy_state?: string;
  taxonomy_license?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  phone?: string;
}

/** Compose a single-line practice address from NPPES location fields. */
export function formatNPPESAddress(r: VerifyNPIResponse): string {
  const cityState = [r.city, r.state].filter(Boolean).join(", ");
  return [r.address_1, r.address_2, cityState, r.postal_code].filter(Boolean).join(", ");
}

/** Verify a provider NPI against the CMS NPPES registry. */
export function verifyNPI(npi: string): Promise<VerifyNPIResponse> {
  return apiPost<VerifyNPIResponse>("/practitioners/verify-npi", { npi });
}

/** Get a paginated list of users */
export function listUsers(params?: UserListParams): Promise<PaginatedResponse<User>> {
  return apiGetList<User>("/users", { params });
}

/** Create a new user */
export function createUser(req: CreateUserRequest): Promise<User> {
  return apiPost<User>("/users", req);
}

/** Get a user by ID */
export function getUser(userId: string): Promise<User> {
  return apiGet<User>(`/users/${userId}`);
}

/** (Compatibility) Get a user by ID */
export const getUserById = getUser;

/** Update a user by ID */
export function updateUser(userId: string, req: UpdateUserRequest): Promise<User> {
  return apiPut<User>(`/users/${userId}`, req);
}

/** Soft delete a user */
export function deleteUser(userId: string): Promise<void> {
  return apiDelete<void>(`/users/${userId}`);
}

/** Clinic admin: unlock a pending / locked / expired account.
 *  For recurring guest windows, unlocks and starts a fresh N-hour period.
 *  Pass clearTimedPolicy to remove the recurring window permanently. */
export function unlockUser(
  userId: string,
  opts?: { clearTimedPolicy?: boolean },
): Promise<User> {
  return apiPost<User>(`/users/${userId}/unlock`, {
    clear_timed_policy: opts?.clearTimedPolicy ?? false,
  });
}

/** Clinic admin: lock account until unlock, or set timed access expiry */
export function lockUser(userId: string, req: LockUserRequest): Promise<User> {
  return apiPost<User>(`/users/${userId}/lock`, req);
}

export interface UserSignatureResponse {
  has_signature: boolean;
  signature_data?: string;
}

/** Get the current user's saved e-signature. */
export function getMySignature(): Promise<UserSignatureResponse> {
  return apiGet<UserSignatureResponse>("/users/me/signature");
}

/** Save or replace the current user's reusable e-signature (data URL). */
export function putMySignature(signatureData: string): Promise<UserSignatureResponse> {
  return apiPut<UserSignatureResponse>("/users/me/signature", {
    signature_data: signatureData,
  });
}

/** Remove the current user's saved e-signature. */
export function deleteMySignature(): Promise<void> {
  return apiDelete<void>("/users/me/signature");
}

/** (Compatibility) Look up a generic array of users */
export function getUsersByIds(userIds: string[]): Promise<User[]> {
  return apiGet<User[]>("/users", { params: { ids: userIds.join(",") } });
}

export interface AuditLog {
  id: string;
  event_type: string;
  action: string;
  user_id: string;
  /** Resolved at read time from the users table. */
  user_name?: string;
  user_email?: string;
  user_role?: string;
  /** Patient whose record was touched (ASTM E2147 §7.1.2). */
  patient_id?: string;
  patient_name?: string;
  patient_mrn?: string;
  organization_id: string;
  resource_type: string;
  resource_id: string;
  resource_description?: string;
  status: string;
  http_status?: number;
  error_code?: string;
  ip_address: string;
  user_agent?: string;
  request_method?: string;
  request_path?: string;
  old_value?: any;
  new_value?: any;
  phi_accessed?: boolean;
  phi_fields?: string;
  previous_hash?: string;
  entry_hash?: string;
  created_at: string;
}

export interface AuditLogParams {
  page?: number;
  per_page?: number;
  event_type?: string;
  action?: string;
  user_id?: string;
  patient_id?: string;
  resource_type?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
}

export interface AuditStats {
  total: number;
  phi_access: number;
  disclosures: number;
  failures: number;
  since: string;
}

/** Event counts for the audit dashboard header. */
export function getAuditStats(days = 30): Promise<AuditStats> {
  // apiGet already unwraps the { data } envelope.
  return apiGet<AuditStats>("/admin/audit-logs/stats", { params: { days } });
}

/** Download the filtered audit trail as CSV (max 10,000 rows). */
export async function exportAuditLogsCsv(
  params: Omit<AuditLogParams, "page" | "per_page">,
): Promise<{ blob: Blob; filename: string }> {
  const apiClient = (await import("./client")).default;
  const response = await apiClient.get<Blob>("/admin/audit-logs/export", {
    params: { ...params, format: "csv" },
    responseType: "blob",
  });
  const disposition = response.headers["content-disposition"] as string | undefined;
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return { blob: response.data, filename: match?.[1] ?? "audit-logs.csv" };
}

/** Get Audit Logs for User */
export function getUserAuditLogs(userId: string, page = 1, perPage = 20): Promise<PaginatedResponse<AuditLog>> {
  return apiGetList<AuditLog>(`/admin/audit-logs/user/${userId}`, {
    params: { page, per_page: perPage },
  });
}

/** List all Audit Logs (Admin) */
export function listAuditLogs(params?: AuditLogParams): Promise<PaginatedResponse<AuditLog>> {
  return apiGetList<AuditLog>("/admin/audit-logs", { params });
}

/** Get single Audit Log entry */
export function getAuditLog(id: string): Promise<AuditLog> {
  return apiGet<AuditLog>(`/admin/audit-logs/${id}`);
}

export interface UserLocationSummary {
  id: string;
  name: string;
  code: string;
}

export interface UserLocationsResponse {
  facilities: UserLocationSummary[];
  default_facility_id?: string | null;
  all_locations: boolean;
}

export interface ReplaceUserLocationsRequest {
  facility_ids: string[];
  default_facility_id?: string | null;
}

export function getUserLocations(userId: string): Promise<UserLocationsResponse> {
  return apiGet<UserLocationsResponse>(`/users/${userId}/locations`);
}

export function replaceUserLocations(
  userId: string,
  req: ReplaceUserLocationsRequest,
): Promise<UserLocationsResponse> {
  return apiPut<{ data: UserLocationsResponse }>(`/users/${userId}/locations`, req).then((res) => res.data);
}

export interface FacilityHoursDay {
  enabled: boolean;
  start: string;
  end: string;
}

export interface FacilityHoursResponse {
  facility_id: string;
  uses_global_hours: boolean;
  schedule: Record<string, FacilityHoursDay>;
}

export interface ReplaceFacilityHoursRequest {
  use_global_hours: boolean;
  schedule: Record<string, FacilityHoursDay>;
}

export function getUserFacilityHours(userId: string, facilityId: string): Promise<FacilityHoursResponse> {
  return apiGet<{ data: FacilityHoursResponse }>(`/users/${userId}/locations/${facilityId}/hours`).then(
    (res) => res.data,
  );
}

export function replaceUserFacilityHours(
  userId: string,
  facilityId: string,
  req: ReplaceFacilityHoursRequest,
): Promise<FacilityHoursResponse> {
  return apiPut<{ data: FacilityHoursResponse }>(`/users/${userId}/locations/${facilityId}/hours`, req).then(
    (res) => res.data,
  );
}
